import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, Container, Typography, Button, Grid } from '@mui/material';
import {
  QrCode2,
  CurrencyRupee,
  ShowChart,
  EventSeat,
  DashboardOutlined,
  LanguageOutlined,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import FloatingBlobs from '../components/FloatingBlobs';

const DEFAULT_WA_URL =
  'https://wa.me/918595675155?text=Hi%20BookMyLibrary%2C%20I%27m%20interested%20in%20listing%20my%20library';

const DEFAULT_ROTATING_WORDS = ['Grow', 'Scale', 'Automate', 'Digitize'];
const ROTATE_INTERVAL_MS = 2400;

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 32 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay, ease: 'easeOut' },
});

/**
 * Cycles through words with a fade-slide transition.
 */
function RotatingWord({ orange, golden, words }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % words.length);
    }, ROTATE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [words]);

  return (
    <Box
      component="span"
      sx={{ display: 'inline-block', position: 'relative', minWidth: '3ch' }}
    >
      <AnimatePresence mode="wait">
        <motion.span
          key={words[index]}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -16 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
          style={{
            display: 'inline-block',
            background: `linear-gradient(135deg, ${orange}, ${golden})`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            color: 'transparent',
          }}
        >
          {words[index]}
        </motion.span>
      </AnimatePresence>
    </Box>
  );
}

RotatingWord.propTypes = {
  orange: PropTypes.string.isRequired,
  golden: PropTypes.string.isRequired,
  words: PropTypes.arrayOf(PropTypes.string).isRequired,
};

/**
 * Seat grid mock — 8 columns × 4 rows of tiny squares.
 */
function SeatGrid({ orange, grey }) {
  const seats = Array.from({ length: 32 }, (_, i) => i);
  const filled = new Set([0, 1, 2, 4, 5, 8, 9, 10, 16, 17, 20, 24]);
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(8, 1fr)',
        gap: 0.5,
      }}
      aria-label="Library seat availability grid"
    >
      {seats.map((i) => (
        <Box
          key={i}
          sx={{
            width: 18,
            height: 18,
            borderRadius: '4px',
            bgcolor: filled.has(i) ? orange : grey,
            transition: 'background 0.2s',
          }}
        />
      ))}
    </Box>
  );
}

SeatGrid.propTypes = {
  orange: PropTypes.string.isRequired,
  grey: PropTypes.string.isRequired,
};

/**
 * Dashboard card mock displayed in the hero right column.
 */
function DashboardMock({ totalCount }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const orange = theme.palette.brand.orange.main;
  const golden = theme.palette.brand.golden.main;
  const navy = theme.palette.brand.navy.main;

  const cardBg = isDark ? 'rgba(27,54,93,0.9)' : '#fff';
  const greySquare = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(27,54,93,0.12)';

  const liveLine =
    totalCount > 0 ? `${totalCount}+ libraries live` : 'Join a growing network';

  return (
    <Box
      sx={{
        background: cardBg,
        borderRadius: '24px',
        p: 3,
        boxShadow: isDark
          ? '0 24px 64px rgba(0,0,0,0.5)'
          : '0 24px 64px rgba(27,54,93,0.18)',
        border: isDark
          ? '1px solid rgba(255,255,255,0.1)'
          : '1px solid rgba(27,54,93,0.08)',
        maxWidth: 380,
        mx: 'auto',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
          Seat Overview
        </Typography>
        <Box
          sx={{
            px: 1.5,
            py: 0.5,
            borderRadius: '999px',
            background: `linear-gradient(135deg, ${orange}, ${golden})`,
            color: '#fff',
            fontSize: '0.7rem',
            fontWeight: 700,
          }}
        >
          LIVE
        </Box>
      </Box>

      {/* Seat grid */}
      <SeatGrid orange={orange} grey={greySquare} />

      {/* Legend */}
      <Box sx={{ display: 'flex', gap: 2, mt: 1.5, mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: orange }} />
          <Typography variant="caption" color="text.secondary">Occupied</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: greySquare }} />
          <Typography variant="caption" color="text.secondary">Available</Typography>
        </Box>
      </Box>

      {/* Ready to earn tile */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 2,
          borderRadius: '14px',
          background: isDark
            ? 'rgba(242,101,34,0.12)'
            : 'rgba(242,101,34,0.06)',
          mb: 2,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '10px',
            background: `linear-gradient(135deg, ${orange}, ${golden})`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CurrencyRupee sx={{ color: '#fff', fontSize: 20 }} />
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Your earning potential
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'text.primary', lineHeight: 1 }}>
            Ready to earn
          </Typography>
        </Box>
        <ShowChart sx={{ ml: 'auto', color: orange, opacity: 0.7, fontSize: 28 }} />
      </Box>

      {/* Libraries live chip + bottom row */}
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Box
          sx={{
            flex: 1,
            p: 1.5,
            borderRadius: '12px',
            background: isDark
              ? 'rgba(27,54,93,0.5)'
              : 'rgba(27,54,93,0.04)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <QrCode2 sx={{ color: navy, fontSize: 28 }} />
          <Typography variant="caption" color="text.secondary" align="center">
            QR Check-in
          </Typography>
        </Box>
        <Box
          sx={{
            flex: 2,
            p: 1.5,
            borderRadius: '12px',
            background: isDark
              ? 'rgba(27,54,93,0.5)'
              : 'rgba(27,54,93,0.04)',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {/* Pulsing green dot */}
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: '#22c55e',
              flexShrink: 0,
              '@keyframes libraryPulse': {
                '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                '50%': { opacity: 0.5, transform: 'scale(1.4)' },
              },
              animation: 'libraryPulse 2s ease-in-out infinite',
            }}
            aria-hidden="true"
          />
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', lineHeight: 1.2 }}
            >
              {liveLine}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
              <EventSeat sx={{ color: navy, fontSize: 14 }} />
              <Typography variant="caption" color="text.secondary">
                Seats
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

DashboardMock.propTypes = {
  totalCount: PropTypes.number,
};

DashboardMock.defaultProps = {
  totalCount: 0,
};

/**
 * Two-column hero section with floating blobs and rotating headline word.
 */
function HeroSection({
  totalCount,
  eyebrow,
  rotatingWords,
  headingPrefix,
  headingSuffix,
  body,
  primaryCtaLabel,
  secondaryCtaLabel,
  whatsappUrl,
}) {
  const theme = useTheme();
  const navigate = useNavigate();
  const orange = theme.palette.brand.orange.main;
  const golden = theme.palette.brand.golden.main;

  const primaryCtaRef = useRef(null);
  const [showStickyCta, setShowStickyCta] = useState(false);

  const handleScrollToSignup = () => {
    const el = document.getElementById('signup');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  // Reveal the sticky bottom CTA once the hero's primary button scrolls out of view.
  useEffect(() => {
    const target = primaryCtaRef.current;
    if (!target || typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => setShowStickyCta(!entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, []);

  return (
    <>
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        minHeight: { xs: 'auto', md: '90vh' },
        display: 'flex',
        alignItems: 'center',
        py: { xs: 8, md: 4 },
      }}
    >
      {/* Floating background blobs — behind content */}
      <FloatingBlobs variant="hero" />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">
          {/* Left column */}
          <Grid item xs={12} md={6}>
            <motion.div {...fadeUp(0)}>
              <Typography
                variant="overline"
                sx={{
                  color: orange,
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  fontSize: '0.75rem',
                  mb: 1,
                  display: 'block',
                }}
              >
                {eyebrow}
              </Typography>
            </motion.div>

            <motion.div {...fadeUp(0.1)}>
              <Typography
                component="h1"
                variant="h2"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '2.25rem', md: '3.5rem' },
                  lineHeight: 1.1,
                  color: 'text.primary',
                  mb: 2.5,
                  letterSpacing: '-0.02em',
                }}
              >
                {headingPrefix}{' '}
                <RotatingWord orange={orange} golden={golden} words={rotatingWords} />{' '}
                <Box component="span" sx={{ color: 'text.primary' }}>
                  {headingSuffix}
                </Box>
              </Typography>
            </motion.div>

            <motion.div {...fadeUp(0.2)}>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  fontSize: { xs: '1rem', md: '1.125rem' },
                  lineHeight: 1.75,
                  mb: 4,
                  maxWidth: 480,
                }}
              >
                {body}
              </Typography>
            </motion.div>

            <motion.div {...fadeUp(0.3)}>
              {/* Primary CTAs — first row */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
                <Button
                  ref={primaryCtaRef}
                  variant="contained"
                  size="large"
                  onClick={handleScrollToSignup}
                  sx={{
                    background: `linear-gradient(135deg, ${orange}, ${golden})`,
                    color: '#fff',
                    fontWeight: 700,
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                    borderRadius: 3,
                    boxShadow: '0 8px 24px rgba(242,101,34,0.35)',
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.brand.orange.light}, ${orange})`,
                      boxShadow: '0 12px 32px rgba(242,101,34,0.45)',
                      transform: 'translateY(-1px)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  {primaryCtaLabel}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  component="a"
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    borderColor: orange,
                    color: orange,
                    fontWeight: 700,
                    px: 3,
                    py: 1.5,
                    fontSize: '1rem',
                    borderRadius: 3,
                    borderWidth: 2,
                    '&:hover': {
                      borderColor: orange,
                      borderWidth: 2,
                      background: 'rgba(242,101,34,0.06)',
                    },
                  }}
                >
                  {secondaryCtaLabel}
                </Button>
              </Box>

              {/* Demo CTAs — second row (outlined, matching the WhatsApp button) */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<DashboardOutlined />}
                  onClick={() => navigate('/demo/library-crm')}
                  sx={{
                    borderColor: orange,
                    color: orange,
                    fontWeight: 700,
                    px: 3,
                    py: 1.5,
                    fontSize: '1rem',
                    borderRadius: 3,
                    borderWidth: 2,
                    '&:hover': {
                      borderColor: orange,
                      borderWidth: 2,
                      background: 'rgba(242,101,34,0.06)',
                    },
                  }}
                >
                  Try Library CRM
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<LanguageOutlined />}
                  onClick={() => navigate('/demo/library-website')}
                  sx={{
                    borderColor: orange,
                    color: orange,
                    fontWeight: 700,
                    px: 3,
                    py: 1.5,
                    fontSize: '1rem',
                    borderRadius: 3,
                    borderWidth: 2,
                    '&:hover': {
                      borderColor: orange,
                      borderWidth: 2,
                      background: 'rgba(242,101,34,0.06)',
                    },
                  }}
                >
                  Library website demo
                </Button>
              </Box>
            </motion.div>
          </Grid>

          {/* Right column — dashboard mock */}
          <Grid item xs={12} md={6}>
            <motion.div {...fadeUp(0.15)}>
              <DashboardMock totalCount={totalCount} />
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>

      {/* Sticky bottom CTA — appears once the hero's primary button scrolls out of view */}
      <AnimatePresence>
        {showStickyCta && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            style={{
              position: 'fixed',
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1200,
              display: 'flex',
              justifyContent: 'center',
              padding: '12px 16px',
              paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))',
              pointerEvents: 'none',
            }}
          >
            <Button
              variant="contained"
              size="large"
              onClick={handleScrollToSignup}
              sx={{
                pointerEvents: 'auto',
                width: { xs: '100%', sm: 'auto' },
                maxWidth: 420,
                background: `linear-gradient(135deg, ${orange}, ${golden})`,
                color: '#fff',
                fontWeight: 700,
                px: 5,
                py: 1.5,
                fontSize: '1rem',
                borderRadius: 3,
                boxShadow: '0 12px 32px rgba(242,101,34,0.45)',
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.brand.orange.light}, ${orange})`,
                  boxShadow: '0 16px 40px rgba(242,101,34,0.55)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              {primaryCtaLabel}
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

HeroSection.propTypes = {
  totalCount: PropTypes.number,
  eyebrow: PropTypes.string,
  rotatingWords: PropTypes.arrayOf(PropTypes.string),
  headingPrefix: PropTypes.string,
  headingSuffix: PropTypes.string,
  body: PropTypes.string,
  primaryCtaLabel: PropTypes.string,
  secondaryCtaLabel: PropTypes.string,
  whatsappUrl: PropTypes.string,
};

HeroSection.defaultProps = {
  totalCount: 0,
  eyebrow: 'For library owners',
  rotatingWords: DEFAULT_ROTATING_WORDS,
  headingPrefix: 'Fill every seat.',
  headingSuffix: 'your library.',
  body:
    'BookMyLibrary helps study libraries across India get discovered by students, fill seats with upfront digital bookings, accept payments seamlessly, and automate daily attendance — all from one mobile-first platform.',
  primaryCtaLabel: 'Start your trial',
  secondaryCtaLabel: 'Chat on WhatsApp',
  whatsappUrl: DEFAULT_WA_URL,
};

export default HeroSection;
